<script setup lang="ts">
import { computed, inject, nextTick, onActivated, onDeactivated, provide, shallowReactive, shallowRef, unref, watch, type VNode } from 'vue';
import { matchedRouteKey, routeLocationKey, routerViewLocationKey, START_LOCATION, viewDepthKey, type RouteLocationNormalizedLoaded } from 'vue-router';
import { primaryPageScrollMemory } from '@/router/primary-page-scroll.ts';

const props = defineProps<{
  component: VNode;
  contentClass?: string;
  route: RouteLocationNormalizedLoaded;
}>();

const route = shallowRef(props.route);
let active = true;

watch(
  () => props.route,
  (value) => {
    route.value = value;
  },
);

const reactiveRoute = {} as RouteLocationNormalizedLoaded;
for (const key in START_LOCATION) {
  Object.defineProperty(reactiveRoute, key, {
    enumerable: true,
    get: () => route.value[key as keyof RouteLocationNormalizedLoaded],
  });
}

const depth = inject(viewDepthKey, 0);
provide(routeLocationKey, shallowReactive(reactiveRoute));
provide(routerViewLocationKey, route);
provide(
  matchedRouteKey,
  computed(() => route.value.matched[Math.max(0, unref(depth) - 1)]),
);

onDeactivated(() => {
  active = false;
});

onActivated(async () => {
  active = true;
  const scrollPosition = primaryPageScrollMemory.position(props.route);
  if (!scrollPosition) return;
  await nextTick();
  requestAnimationFrame(() => {
    if (active) window.scrollTo(scrollPosition);
  });
});
</script>

<template>
  <div
    class="route-view-page flex w-full min-w-0 flex-col"
    :class="contentClass"
  >
    <component :is="component" />
  </div>
</template>
